import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

export interface Money {
  amount: number;
  currency: string;
}

export interface InventoryVariant {
  id: string;
  name: string;
  description?: string;
  longDescription?: string;
  trackStock: boolean;
  reorderThreshold?: number;
  price?: Money;
  cogs?: Money;
  quantity?: number;
  active: boolean;
}

export interface InventoryItem {
  id: string;
  venueId: string;
  name: string;
  description?: string;
  trackStock: boolean;
  variants: InventoryVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductInput {
  groupName: string;
  groupDescription?: string;
  productName: string;
  quantity?: number;
  price: number;
  cogs?: number;
}

export interface AddVariantInput {
  name: string;
  quantity?: number;
  price: number;
  cogs?: number;
}

export interface EditVariantInput {
  name: string;
  quantity?: number;
  price: number;
  cogs?: number;
}

const listKey = (venueId: string) => ['inventory-items', venueId] as const;

const getInventoryItems = (venueId: string): Promise<InventoryItem[]> =>
  (
    unwrap(
      apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/inventory-items', {
        params: { path: { venueId } },
      }),
    ) as Promise<{ items: InventoryItem[] }>
  ).then((data) => data.items ?? []);

export const createProduct = (venueId: string, input: CreateProductInput): Promise<InventoryItem> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/inventory-items', {
      params: { path: { venueId } },
      body: input,
    }),
  ) as Promise<InventoryItem>;

export const editVariant = (
  venueId: string,
  itemId: string,
  variantId: string,
  input: EditVariantInput,
): Promise<InventoryItem> =>
  unwrap(
    apiClient.PATCH(
      '/api/v1/venue-workspace/venues/{venueId}/inventory-items/{itemId}/variants/{variantId}',
      {
        params: { path: { venueId, itemId, variantId } },
        body: input,
      },
    ),
  ) as Promise<InventoryItem>;

export function useInventoryItems(venueId: string | undefined) {
  return useQuery({
    queryKey: ['inventory-items', venueId ?? ''],
    queryFn: () => getInventoryItems(venueId as string),
    enabled: Boolean(venueId),
  });
}

export function useCreateProduct(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => createProduct(venueId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey(venueId) }),
  });
}

export function useEditVariant(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { itemId: string; variantId: string; input: EditVariantInput }) =>
      editVariant(venueId, args.itemId, args.variantId, args.input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey(venueId) }),
  });
}

const addVariantToGroup = (
  venueId: string,
  itemId: string,
  input: AddVariantInput,
): Promise<InventoryItem> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/inventory-items/{itemId}/variants', {
      params: { path: { venueId, itemId } },
      body: input,
    }),
  ) as Promise<InventoryItem>;

export function useAddVariant(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { itemId: string; input: AddVariantInput }) =>
      addVariantToGroup(venueId, args.itemId, args.input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey(venueId) }),
  });
}
